// ============================================================================
// chatAttachmentUploads.ts — реестр передач композера вложений чата.
//   Один File → одна передача chunkedUpload (защита от двойного запуска
//   эффекта в StrictMode). jobId здесь — КЛЮЧ в менеджере передач: сервер
//   может выдать новый uploadId, но запись в реестре остаётся под исходным
//   ключом, поэтому ключ нельзя заменять на job.id.
// ============================================================================

import { getUploadJobs, uploadFile, type UploadJob } from '@/lib/chunkedUpload';

export interface UploadRecord {
  jobId: string;
  promise: Promise<UploadJob>;
}

const uploadRegistry = new WeakMap<File, UploadRecord>();

export function ensureUpload(file: File): UploadRecord {
  const existing = uploadRegistry.get(file);
  if (existing) return existing;
  const before = new Set(getUploadJobs().map((j) => j.id));
  const promise = uploadFile(file);
  // uploadFile создаёт job синхронно (jobs.set + emit) ДО первого await —
  // поэтому id передачи доступен сразу после вызова.
  const created = getUploadJobs().find((j) => !before.has(j.id));
  const record: UploadRecord = { jobId: created?.id || '', promise };
  uploadRegistry.set(file, record);
  return record;
}

export function forgetUpload(file: File): void {
  uploadRegistry.delete(file);
}

export type EntryStatus = 'uploading' | 'queued' | 'ready' | 'error';

export interface Entry {
  key: string;
  file: File;
  name: string;
  size: number;
  type: string;
  status: EntryStatus;
  progress: number;
  jobId?: string;
  fileId?: string;
  error?: string;
}

/** Перенос статуса передачи в элемент композера (jobId не меняется). */
export function jobToEntry(entry: Entry, job: UploadJob | undefined): Entry {
  if (!job) return entry;
  let status: EntryStatus = 'uploading';
  let error: string | undefined;
  if (job.status === 'done') {
    if (job.fileId) {
      status = 'ready';
    } else {
      status = 'error';
      error = 'Сервер не вернул идентификатор файла';
    }
  } else if (job.status === 'error' || job.status === 'aborted') {
    status = 'error';
    error = job.error || 'Не удалось загрузить файл';
  } else if (job.status === 'queued') {
    status = 'queued';
  }
  const progress = status === 'ready' ? 1 : Math.min(1, Math.max(0, job.progress || 0));
  if (
    entry.status === status && entry.progress === progress
    && entry.fileId === job.fileId && entry.error === error
  ) {
    return entry;
  }
  return { ...entry, status, progress, fileId: job.fileId, error };
}
